import {
  AssistantAiModel,
  AssistantCreateFlowModel,
  AssistantCreateModel,
  AssistantDetailedModel,
  AssistantListModel,
  AssistantRecordModel,
  AssistantTypeModel,
  AssistantPatienceLevelModel,
  AssistantSpeechRecognitionLevelModel,
  AssistantCompactModel,
  AssistantEvenCompactModel,
  AssistantVoiceLanguageModel,
  AssistantTranscriberProviderModel,
  AssistantPromptingTypeModel,
} from "../../models";
import {
  Assistant,
  AssistantLlm,
  AssistantPromptingType,
  AssistantSummary,
  AssistantTranscriberProvider,
  AssistantVoiceSynthesizer,
  CreateAssistantFromTemplateBody,
  GetAssistantList200,
  NameCount,
} from "../../services";
import {datesTransform, imagesTransform} from "../common";
import {phoneNumbersTransform} from "../PhoneNumbersTransform";
import {ManagementTransformBase} from "../TranformBase";
import {workspacesTransform} from "../WorkspacesTransform";
import {assistantBackgroundNoisesTransform} from "./AssistantBackgroundNoisesTransform";
import {assistantExternalWebhookTransform} from "./AssistantExternalWebhookTransform";
import {assistantRemindersTransform} from "./AssistantRemindersTransform";
import {assistantSynthesizersTransform} from "./AssistantSynthesizersTransform";
import {assistantVoicesTransform} from "./AssistantVoicesTransform";
import {knowledgeBasesTransform} from "../knowledge-bases";

import {DEFAULT_SUPPRESSION_LEVEL} from "@/constants/assistants";
import {
  VECTOR_DATABASE_THRESHOLD,
  VECTOR_DATABASE_MAX_RESULTS,
} from "@/constants/knowledgeBases";
import {AssistantPartialUpdateModel} from "@/utils/assistants/data/models";

export class AssistantsTransform extends ManagementTransformBase<AssistantRecordModel> {
  public makeTypeModel(dto: string): AssistantTypeModel {
    switch (dto) {
      case "inbound":
        return AssistantTypeModel.INBOUND;
      case "outbound":
        return AssistantTypeModel.OUTBOUND;
      case "widget":
        return AssistantTypeModel.WIDGET;
      default:
        console.warn(`Unknown agent type: ${dto}`);

        return AssistantTypeModel.INBOUND;
    }
  }

  public makeTypeDto(model: AssistantTypeModel): string {
    switch (model) {
      case AssistantTypeModel.INBOUND:
        return "inbound";
      case AssistantTypeModel.OUTBOUND:
        return "outbound";
      case AssistantTypeModel.WIDGET:
        return "widget";
      default:
        console.warn(`Unknown agent type: ${model}`);

        return "inbound";
    }
  }

  public makeRecordModel(dto: Assistant): AssistantRecordModel {
    const model: AssistantRecordModel = {
      id: dto.model_id,
      ...datesTransform.makeTimestampsModel(dto),
      name: dto.agent_name,
      type: this.makeTypeModel(dto.agent_type),
      voice: assistantVoicesTransform.makeEvenCompactModel(
        dto.voice_engine_type,
      ),
      phoneNumber: phoneNumbersTransform.makeEvenCompactModel(dto),
      avatar: imagesTransform.makeImageModel(dto.image_url),
    };

    return model;
  }

  public makeListModel(dto: GetAssistantList200): AssistantListModel {
    const model: AssistantListModel = {
      records: dto.items.map(item => this.makeRecordModel(item as Assistant)),
      pagination: this.makePaginationModel(dto),
    };

    return model;
  }

  private mapEndpointingToSpeechRecognitionLevel(
    endpointing?: number,
  ): AssistantSpeechRecognitionLevelModel {
    const endpointingValue = endpointing || 0;
    // If the endpointing is closer to 100, return FASTER, if it's closer to 300, return HIGH_ACCURACY
    return Math.abs(endpointingValue - 100) <= Math.abs(endpointingValue - 300)
      ? AssistantSpeechRecognitionLevelModel.FASTER
      : AssistantSpeechRecognitionLevelModel.HIGH_ACCURACY;
  }

  public makeDetailedModel(dto: Assistant): AssistantDetailedModel {
    const model: AssistantDetailedModel = {
      id: dto.model_id,
      ...datesTransform.makeTimestampsModel(dto),
      ...workspacesTransform.makeThingModel(dto),

      name: dto.agent_name,
      avatar: imagesTransform.makeImageModel(dto.image_url),
      type: this.makeTypeModel(dto.agent_type),
      voice: assistantVoicesTransform.makeCompactModel(dto),
      synthesizer: {
        ...assistantSynthesizersTransform.makeCompactModel(dto),
        voicePrompt: (() => {
          const [pronoun, text] = (dto.synthesizer_voice_prompt || "").split(
            " said ",
          );
          return {
            pronoun: (pronoun as "he" | "she") || "he",
            text: text || "",
          };
        })(),
      },
      externalWebhook: assistantExternalWebhookTransform.makeCompactModel(dto),
      phoneNumber: phoneNumbersTransform.makeCompactModel(dto),
      backgroundNoise: assistantBackgroundNoisesTransform.makeCompactModel(dto),
      reminder: {
        ...assistantRemindersTransform.makeCompactModel(dto),
        isEnabled: Boolean(dto.send_user_idle_reminder),
      },
      fadeOutAtInterruption: Number(dto.num_fade_out_frames ?? 0),
      isKrispEnabled: Boolean(dto.krisp_enabled),
      voiceIsolationLevel: Math.round((dto.suppression_level ?? 0.5) * 100),
      voiceSpeed: Number(dto.synthesizer_eleven_labs_speed ?? 1),
      voiceVolume: Number(dto.agent_volume ?? 1) * 100,
      minWordsToInterrupt: Number(dto.transcriber_min_words_to_interrupt ?? 0),

      // INFO: not sure about these fields.
      timezone: dto.agent_timezone,
      isLowRiskPermissionsDisabled: dto.is_low_risk_permissions_disabled,
      isPublic: dto.is_public,
      isRecording: dto.is_recording,
      isTemplate: dto.is_template,
      isTranscriptEnabled: !dto.is_transcript_disabled,
      isDataRetentionEnabled: dto.data_retention,
      isConsentEnabled: dto.is_consent_enabled,
      consentMessage: dto.consent_message,
      urlWssWidget: dto.url_wss_widget,
      temperature: dto.temperature,
      tier: dto.tier,
      transcriberKeywords: dto.transcriber_keywords,
      transcriberProvider:
        dto.transcriber_provider as AssistantTranscriberProviderModel,
      useAmdFromTelephonyProvider: dto.use_amd_from_telephony_provider,
      allowedIdleTimeSeconds: dto.allowed_idle_time_seconds,
      callerId: dto.caller_id,
      campaignId: dto.campaign_id,
      language:
        (dto.language as AssistantVoiceLanguageModel) ||
        AssistantVoiceLanguageModel.ENGLISH,
      greetingMessage: dto.greeting_message || "",
      initialPauseSeconds: dto.initial_pause_seconds,
      ringDuration: dto.ring_pause_seconds || 0,
      aiModel:
        dto.llm === "gpt-4o" && dto.agent_type === "widget"
          ? AssistantAiModel.SYNTHFLOW
          : (dto.llm as AssistantAiModel),
      maxDurationIsEnabled: dto.max_duration_is_enabled,
      maxDurationSeconds: dto.max_duration_seconds,
      patienceLevel: dto.patience_level as AssistantPatienceLevelModel,
      speechRecognitionLevel: this.mapEndpointingToSpeechRecognitionLevel(
        dto.transcriber_endpointing,
      ),
      personality: dto.personality,
      prompt: dto.prompt || "",
      rateLimitPerMinute: dto.rate_limit_per_minute,
      sendTestCallWebhook: dto.send_test_call_webhook,
      sendUserIdleReminder: dto.send_user_idle_reminder,
      sipDomain: dto.sip_domain,
      sipOutboundTrunkId: dto.sip_outbound_trunk_id,
      knowledgeBase: dto.knowledge_base
        ? knowledgeBasesTransform.makeRecordModel(
            dto.knowledge_base as NameCount,
          )
        : null,
      vectorDatabase:
        dto.vector_database_collection_id && dto.vector_database_collection_name
          ? {
              id: dto.vector_database_collection_id,
              name: dto.vector_database_collection_name,
            }
          : null,
      inboundWebhookUrl: dto.inbound_call_webhook_url || null,
      widgetId: dto.widget_id || null,
      widgetVoiceId: dto.voice_id || null,
      promptingType: dto.prompting_type as AssistantPromptingTypeModel,
      featureFlags: dto.feature_flags,
      memoryStoreId: dto.memory_store_id,
    };

    return model;
  }

  public makeCreateDto(model: AssistantCreateModel): {
    data: CreateAssistantFromTemplateBody;
  } {
    let templateId;

    if (model.flow === AssistantCreateFlowModel.FROM_SCRATCH) {
      templateId = this.makeTypeDto(model.data.type);
    } else {
      templateId = model.data.template.id;
    }

    const data: CreateAssistantFromTemplateBody = {
      template_id: templateId,
      variables_to_overwrite: {
        workspace_id: model.meta.workspaceId,
        krisp_enabled: false,
        ...(model.meta.widgetId && {
          widget_id: model.meta.widgetId,
        }),
      },
    };

    return {data};
  }

  public makeUpdateDto(model: AssistantPartialUpdateModel): Assistant {
    const dto: Assistant = {};

    // Basic Information
    if ("name" in model) dto.agent_name = model.name;
    // if ("type" in model) dto.agent_type = this.makeTypeDto(model.type);

    // AI and Language Settings
    if ("aiModel" in model) dto.llm = model.aiModel as AssistantLlm;
    if ("timezone" in model) dto.agent_timezone = model.timezone;
    if ("prompt" in model) dto.prompt = model.prompt;
    // if ("personality" in model) dto.personality = model.personality;
    if ("temperature" in model) dto.temperature = model.temperature;
    if ("transcriberKeywords" in model)
      dto.transcriber_keywords = model.transcriberKeywords;
    if ("transcriberProvider" in model)
      dto.transcriber_provider =
        model.transcriberProvider as AssistantTranscriberProvider;

    // Voice Configuration
    if ("voice" in model) {
      const {voice} = model;

      if (voice?.id) {
        dto.voice_id = voice.id;
      }
      if (voice?.name) {
        dto.voice_name = voice.name;
      }
      if (voice?.audioUrl) {
        dto.voice_preview_url = voice.audioUrl;
      }
      if (voice?.gender) {
        dto.voice_gender = voice.gender;
      }
      if (voice?.accent) {
        dto.voice_accent = voice.accent;
      }
      if (voice?.engine?.type) {
        dto.voice_engine_type = voice.engine.type;
      }
      if (voice?.synthesizer) {
        dto.voice_synthesizer =
          voice.synthesizer as unknown as AssistantVoiceSynthesizer;
      }
    }
    if ("widgetVoiceId" in model) {
      dto.voice_id = model.widgetVoiceId;
      dto.voice_name = model.widgetVoiceId;
    }

    if ("fadeOutAtInterruption" in model) {
      dto.num_fade_out_frames = model.fadeOutAtInterruption;
    }

    if ("isKrispEnabled" in model) {
      dto.krisp_enabled = model.isKrispEnabled;
      // When isKrispEnabled is true (Standard), suppression_level should be null
      // When isKrispEnabled is false (Voice Isolation), suppression_level uses voiceIsolationLevel
      if (model.isKrispEnabled) {
        // When switching to Voice Isolation, use the voiceIsolationLevel value
        const level =
          Number(model.voiceIsolationLevel) || DEFAULT_SUPPRESSION_LEVEL;
        dto.suppression_level = level / 100;
      } else {
        dto.suppression_level = null;
      }
    }

    if ("voiceIsolationLevel" in model) {
      const level = Number(model.voiceIsolationLevel) || 0;
      dto.suppression_level = level / 100;
    }

    if ("voiceSpeed" in model) {
      dto.synthesizer_eleven_labs_speed = model.voiceSpeed;
    }

    if ("voiceVolume" in model) {
      dto.agent_volume = model.voiceVolume / 100;
    }

    if ("minWordsToInterrupt" in model) {
      dto.transcriber_min_words_to_interrupt = model.minWordsToInterrupt;
    }

    // Synthesizer Settings
    if ("synthesizer" in model) {
      if ("filterWords" in model.synthesizer) {
        dto.synthesizer_filter_words = model.synthesizer.filterWords;
      }
      if ("useFilterWords" in model.synthesizer) {
        dto.use_filler_words = model.synthesizer.useFilterWords;
      }
      if ("optimizeStreamingLatency" in model.synthesizer) {
        dto.synthesizer_optimize_streaming_latency =
          model.synthesizer.optimizeStreamingLatency;
      }
      if ("similarityBoost" in model.synthesizer) {
        dto.synthesizer_similarity_boost = model.synthesizer.similarityBoost;
      }
      if ("stability" in model.synthesizer) {
        dto.synthesizer_stability = model.synthesizer.stability;
      }
      if ("style" in model.synthesizer) {
        dto.synthesizer_style = model.synthesizer.style;
      }
      if ("voicePrompt" in model.synthesizer) {
        const {pronoun, text} = model.synthesizer.voicePrompt;
        dto.synthesizer_voice_prompt = `${pronoun} said ${text}`;
      }
      if ("speakerBoost" in model.synthesizer) {
        dto.synthesizer_use_speaker_boost = model.synthesizer
          .speakerBoost as boolean;
      }
    }

    // Reminder Settings
    if ("reminder" in model) {
      if ("isEnabled" in model.reminder) {
        dto.send_user_idle_reminder = model.reminder.isEnabled;
      }
      if ("message" in model.reminder) {
        dto.reminder_message = model.reminder.message;
      }
      if ("afterIdleSeconds" in model.reminder) {
        dto.reminder_after_idle_seconds = model.reminder.afterIdleSeconds;
      }
    }

    // Call Duration and Timing Settings
    if ("patienceLevel" in model) dto.patience_level = model.patienceLevel;
    if ("speechRecognitionLevel" in model) {
      if (
        model.speechRecognitionLevel ===
        AssistantSpeechRecognitionLevelModel.FASTER
      ) {
        dto.transcriber_endpointing = 100;
      } else if (
        model.speechRecognitionLevel ===
        AssistantSpeechRecognitionLevelModel.HIGH_ACCURACY
      ) {
        dto.transcriber_endpointing = 300;
      }
    }
    if ("initialPauseSeconds" in model)
      dto.initial_pause_seconds = model.initialPauseSeconds;
    if ("ringDuration" in model)
      dto.ring_pause_seconds = model.ringDuration as number;
    if ("maxDurationIsEnabled" in model)
      dto.max_duration_is_enabled = model.maxDurationIsEnabled;
    if ("maxDurationSeconds" in model)
      dto.max_duration_seconds = model.maxDurationSeconds;
    if ("allowedIdleTimeSeconds" in model)
      dto.allowed_idle_time_seconds = model.allowedIdleTimeSeconds;

    // Call Recording and Transcription
    if ("isRecording" in model) dto.is_recording = model.isRecording;
    if ("isTranscriptEnabled" in model)
      dto.is_transcript_disabled = !model.isTranscriptEnabled;

    if ("isDataRetentionEnabled" in model)
      dto.data_retention = model.isDataRetentionEnabled;

    // Consent Settings
    if ("isConsentEnabled" in model) {
      dto.is_consent_enabled = model.isConsentEnabled;
    }
    if ("consentMessage" in model || "isConsentEnabled" in model) {
      const isConsentEnabled = model.isConsentEnabled ?? true;
      dto.consent_message = isConsentEnabled
        ? model.consentMessage || null
        : null;
    }

    // Rate Limiting and Permissions
    if ("rateLimitPerMinute" in model)
      dto.rate_limit_per_minute = model.rateLimitPerMinute;
    if ("isLowRiskPermissionsDisabled" in model) {
      dto.is_low_risk_permissions_disabled = model.isLowRiskPermissionsDisabled;
    }

    // External Integration Settings
    if ("sendTestCallWebhook" in model)
      dto.send_test_call_webhook = model.sendTestCallWebhook;
    if ("useAmdFromTelephonyProvider" in model) {
      dto.use_amd_from_telephony_provider = model.useAmdFromTelephonyProvider;
    }
    if ("sipDomain" in model) dto.sip_domain = model.sipDomain;
    if ("sipOutboundTrunkId" in model)
      dto.sip_outbound_trunk_id = model.sipOutboundTrunkId;
    if ("backgroundNoise" in model) {
      if (
        "isEnabled" in model.backgroundNoise &&
        (!model.backgroundNoise.isEnabled ||
          (!model.backgroundNoise.type && !model.backgroundNoise.volume))
      ) {
        dto.background_noise_enabled = false;
      } else {
        dto.background_noise_enabled = true;
        if ("type" in model.backgroundNoise) {
          dto.background_noise_type = model.backgroundNoise.type;
        }
        if ("volume" in model.backgroundNoise) {
          dto.background_noise_volume = model.backgroundNoise.volume;
        }
      }
    }

    // Miscellaneous Settings
    if ("isPublic" in model) dto.is_public = model.isPublic;
    if ("greetingMessage" in model)
      dto.greeting_message = model.greetingMessage;
    if ("callerId" in model) dto.caller_id = model.callerId;
    if ("campaignId" in model) dto.campaign_id = model.campaignId;
    // if ("tier" in model) dto.tier = model.tier;
    if ("language" in model) dto.language = model.language;
    if ("externalWebhookUrl" in model)
      dto.external_webhook_url = model.externalWebhookUrl;
    if ("inboundWebhookUrl" in model)
      dto.inbound_call_webhook_url = model.inboundWebhookUrl;

    if ("vectorDatabase" in model) {
      const vectorDb = model.vectorDatabase as {
        id: string;
        name: string;
        rag_use_condition: string;
      } | null;
      if (vectorDb) {
        dto.vector_database_collection_id = vectorDb.id;
        dto.vector_database_collection_name = vectorDb.name;
        dto.vector_database_threshold = VECTOR_DATABASE_THRESHOLD;
        dto.vector_database_max_results = VECTOR_DATABASE_MAX_RESULTS;
        dto.vector_database_rag_use_condition = vectorDb.rag_use_condition;
        dto.vector_database_enabled = true;
      } else {
        dto.vector_database_collection_id = null;
        dto.vector_database_collection_name = null;
        dto.vector_database_rag_use_condition = null;
        dto.vector_database_enabled = false;
      }
    }

    if ("promptingType" in model) {
      dto.prompting_type = model.promptingType as AssistantPromptingType;
    }

    if ("memoryStoreId" in model) {
      dto.memory_store_id = model.memoryStoreId as string | null;
    }

    return dto;
  }

  public makeCompactModel(dto: AssistantSummary): AssistantCompactModel {
    return {
      ...this.makeEvenCompactModel(dto),
      phoneNumber: phoneNumbersTransform.makeEvenCompactModel(dto),
    };
  }

  public makeEvenCompactModel(
    dto: AssistantSummary,
  ): AssistantEvenCompactModel {
    return {
      id: dto.model_id,
      name: dto.name,
      type: this.makeTypeModel(dto.type),
      avatar: imagesTransform.makeImageModel(dto.avatar),
    };
  }
}

export const assistantsTransform = new AssistantsTransform();
